import { CreateSphere, Matrix, Quaternion, TransformNode, Vector3, type Camera, type Mesh, type Node, type Scene } from "@babylonjs/core";
import { CAPSTAN, MODE_SWIM, PLAYER, SHIP, SHIP_POINTS, bodyEyeHeight, capstanBar } from "@tiny-seas/shared";
import { STROKE } from "../audio/gameplay.ts";
import { CHARACTER_TIMING, type CharacterState } from "../character/character-animator.ts";
import { digSwing, type CharacterPose } from "../character/character-pose.ts";
import { gaitFor } from "../character/gait.ts";
import { CARRY_PITCH_SHARE, THIRD_PERSON_ACTION_FRAME, THIRD_PERSON_CARRY, ViewmodelLag, armSwing, carryWeight, createFreeHand, freeHandToRef } from "../character/arm-layers.ts";
import type { Environment } from "./environment.ts";
import { spawnModel } from "./models.ts";
import { spawnScenery } from "./scenery.ts";
import { characterCoatColor, SURFACE_PROFILES, sphereProjectUVs } from "./materials.ts";
import type { CharacterLook } from "@tiny-seas/shared";
import { lookSpawnOptions } from "./character-look.ts";
import { cannonGripToRef, capstanGripToRef, HELM_LEAN, HELM_STEP, HelmHands, ladderGripRelease, ladderGripSlot, ladderGripToRef, ladderHandReaching } from "./station-grips.ts";
import { ArticulatedArm, ArticulatedLeg, HANGING_HAND_GRIP, handRotationToRef, SWORD_GRIP_ROTATION, SWORD_GUARD, SWORD_OFFHAND, PistolMechanism, chestGripToRef, createPistolPose, samplePistolPoseToRef, sampleSwordPoseToRef, spyglassGripToRef, type HandPose, type SwordPose } from "./character-rig.ts";
import { createSwordBody, sampleSwordBodyToRef } from "./sword-body.ts";
import { BUCKET_BASE_GRIP, BUCKET_RIM_GRIP, sampleBucketPoseToRef, type BucketPose } from "./bucket-pose.ts";
import { RepairClock } from "../character/repair-swing.ts";
import { HAMMER_GRIP_ROTATION, REPAIR_BRACE_FINGERS, REPAIR_BRACE_PALM, RepairPose } from "./repair-rig.ts";
const smooth = (t: number): number => { const v = Math.max(0, Math.min(1, t)); return v * v * (3 - 2 * v); };
/**
 * A foot's fore-aft offset in reach units through its stride cycle: planted from +1 to -1 for `duty`,
 * then swung forward. The swing leaves and meets the ground at half the stance's backward slide, so the
 * foot kicks back after toe-off and reaches past its strike point before pawing back onto it.
 */
function strideOffset(cycle: number, duty: number): number {
  const f = cycle - Math.floor(cycle);
  if (f < duty) return 1 - 2 * f / duty;
  const t = (f - duty) / (1 - duty);
  return -1 + 2 * smooth(t) - (1 - duty) / duty * t * (2 * t - 1) * (t - 1);
}

/**
 * Meshes on this layer are invisible to every camera (Babylon's default camera
 * mask is 0x0FFFFFFF) yet still cast shadows: shadow render lists only check
 * layer masks under `forceLayerMaskCheck`.
 */
export const FIRST_PERSON_HIDDEN_LAYER = 0x10000000;
/** First-person feet sit slightly behind the eye so the torso stays out of view. */
const FIRST_PERSON_BODY_BACK = 0.06;
/** Extra offset when looking straight down, so the view clears the chest instead of the collar. */
const FIRST_PERSON_LOOK_DOWN_BACK = 0.24;
/** Neck pivot above the hip pivot (crewmate torso: hips 0.82, neck 1.50). */
const HEAD_ABOVE_HIPS = 0.68;
/** First-person neck pivot below the eye: the standing body (eye 1.62, hips 0.78) unchanged. */
const FIRST_PERSON_NECK_BELOW_EYE = 0.16;
/** Torso-local first-person eye at the helm: standing eye height, just ahead of the neck. */
const FIRST_PERSON_HELM_EYE = { y: 1.66, z: FIRST_PERSON_BODY_BACK } as const;
/** Camera-derived hip limits: legs stay reachable, and never stand taller than upright. */
const FIRST_PERSON_MIN_HIP = 0.05;
const FIRST_PERSON_MAX_HIP = 0.82;
/** Preset eyelids: closing rotation about the shared eye axis, and one blink's length. */
const EYELID_CLOSED = 0.95;
const BLINK_SECONDS = 0.16;
/** Coat panels follow this share of the thigh's forward swing, within these limits. */
const SKIRT_FOLLOW = 0.7;
const SKIRT_MAX_FORWARD = 0.9;
const SKIRT_MAX_BACK = 0.45;
/** First-person shovel carry turn about the view's vertical axis (radians). */
const FIRST_PERSON_SHOVEL_YAW = 0.35;
/**
 * Camera-local origin of the first-person action frame: low enough that held
 * tools clear the eye, high enough that the hands stay in view.
 */
const FIRST_PERSON_ACTION_FRAME = { x: 0, y: -0.1, z: 0.05 } as const;
/**
 * Share of the cutlass torso twist, bend and lean kept in first person: the view kick
 * carries the cut there, and full twist would swing the shoulders under the camera-held hands.
 */
const FIRST_PERSON_SWORD_BODY = 0.4;
/** Torso pivot above its origin: the hip joint the twist, bend and lean turn about. */
const TORSO_HIP_PIVOT = 0.82;
/** Hip sockets either side of the pivot. */
const HIP_HALF_WIDTH = 0.115;

/** Half the angle between the two palms on the cascabel; both keep on their shoulder's side. */
const CANNON_HAND_SPREAD = Math.PI / 4;
/** Palms never slide past the barrel's sides, where the wrap would invert. */
const CANNON_HAND_MAX_AZIMUTH = Math.PI * 85 / 180;
/** Rear station on the authored barrel: the cascabel ball's crown. */
const REMOTE_CANNON_STATION = -0.38;
/** Short slide keeps the palms apart on the ball instead of meeting at its rear tip. */
const REMOTE_CANNON_RECOIL_TRAVEL = 0.02;
/** First-person gunner lean: deep enough that the flare grip below is within reach. */
const FIRST_PERSON_CANNON_LEAN = 0.55;
/** First-person palms brace the rear flare (in view over the breech), sliding back with recoil. */
const FIRST_PERSON_CANNON_GRIP = { azimuth: Math.PI / 180 * 40, station: -0.17, recoilTravel: 0.08 } as const;
/**
 * Fingers brace forward along the barrel and toward the midline. The wrap pole
 * then keeps each elbow between its shoulder and its palm; at ±70° the fingers
 * splayed outward and the forearms crossed beneath the camera.
 */
const FIRST_PERSON_CANNON_WRISTS = [
  handRotationToRef(-1, -2 * Math.PI / 3, 0, 0, new Quaternion()),
  handRotationToRef(1, -2 * Math.PI / 3, 0, 0, new Quaternion()),
] as const;
/** Share of the grip-directed elbow pole for first-person gunners; the rest keeps elbows outboard. */
const FIRST_PERSON_CANNON_GRIP_POLE = 0.7;
/** Repair plank centre in the action frame once it is held over the hole; first person keeps it clear of the hotbar. */
const REPAIR_PLANK = { x: -0.02, y: -0.30, z: 0.48 } as const;
const FIRST_PERSON_REPAIR_PLANK = { x: -0.08, y: -0.08, z: 0.50 } as const;
/**
 * Third-person hull-work kneel: hip height, torso lean, and the action frame's pitch on the
 * leaning torso (0 keeps the plank near upright against the hull wherever the head looks).
 */
const REPAIR_KNEEL = { hip: 0.4, lean: 0.35, pitch: 0 } as const;
/** Capstan pushers lean into their bar; first person less, keeping the bar below the eye. */
const CAPSTAN_LEAN = 0.5;
const FIRST_PERSON_CAPSTAN_LEAN = 0.3;

/** Swimming third-person eye above the feet: the physical swim eye, keeping the head above water. */
const SWIM_EYE = bodyEyeHeight({ mode: MODE_SWIM, crouched: false });
/** Body lean from upright: treading water, heads-up breaststroke, and an idle underwater hover. */
const SWIM_TREAD_LEAN = 0.15;
const SWIM_SURFACE_LEAN = 1.2;
const SWIM_HOVER_LEAN = 0.95;
/** Underwater lean follows the travel direction, at most this far above or below horizontal. */
const SWIM_MAX_TRAVEL_PITCH = 1.15;
/** Tightest neck extension while the swimming body leans forward. */
const SWIM_NECK_LIMIT = -1.0;
/**
 * Breaststroke keys for the right hand in the torso frame (+y spine, +z chest,
 * shoulder at 0.30, 1.40, 0): [phase, position, palm normal, finger direction].
 * Phase 0 is the streamlined glide; the left hand mirrors x.
 */
type SwimKey = readonly [number, number, number, number, number, number, number, number, number, number];
const SURFACE_STROKE: readonly SwimKey[] = [
  [0.00, 0.10, 2.00, 0.14, 0.3, 0, 1, 0, 1, 0],
  [0.30, 0.44, 1.86, 0.20, 0.8, -0.3, 0.6, 0.25, 1, 0.1],
  [0.55, 0.22, 1.42, 0.42, -0.3, -0.8, 0.5, 0.1, 0.3, 1],
  [0.68, 0.07, 1.58, 0.32, -0.8, 0, 0.6, 0, 1, 0.3],
  [0.85, 0.09, 1.92, 0.18, -0.2, 0, 1, 0, 1, 0],
];
/** Underwater: a longer glide and the pull carried through to the thighs. */
const UNDERWATER_STROKE: readonly SwimKey[] = [
  [0.00, 0.08, 2.02, 0.10, 0.2, 0, 1, 0, 1, 0],
  [0.25, 0.38, 1.90, 0.22, 0.7, -0.3, 0.6, 0.2, 1, 0.1],
  [0.45, 0.18, 1.45, 0.40, -0.2, -0.9, 0.4, 0, 0.2, 1],
  [0.62, 0.26, 0.98, 0.16, 0.2, -1, 0, 0, 0, 1],
  [0.80, 0.08, 1.50, 0.32, -0.8, 0, 0.6, 0, 1, 0.3],
  [0.92, 0.08, 1.90, 0.16, -0.2, 0, 1, 0, 1, 0],
];
/**
 * Frog kick for the right foot, sharing the arm phase: legs stay long through the
 * pull, heels draw up as the arms recover, then sweep out and snap together as
 * the arms extend. [phase, lateral, along the trailing leg, dorsal, ankle flex].
 */
type KickKey = readonly [number, number, number, number, number];
const FROG_KICK: readonly KickKey[] = [
  [0.00, 0.09, 0.72, 0.00, 0],
  [0.50, 0.09, 0.72, 0.00, 0],
  [0.72, 0.17, 0.40, 0.30, 1],
  [0.82, 0.40, 0.58, 0.12, 1],
  [0.94, 0.10, 0.72, 0.00, 0.2],
];

/** Interpolated key columns; the rig samples on the render thread only. */
const SWIM_SAMPLE: number[] = Array.from({ length: 10 }, () => 0);

/** Eases between the looping keys around `phase` (0…1; column 0 holds key phases) into SWIM_SAMPLE. */
function sampleLoop(keys: readonly (readonly number[])[], phase: number): number[] {
  let k = keys.length - 1;
  while (k > 0 && keys[k]![0]! > phase) k--;
  const a = keys[k]!, b = keys[(k + 1) % keys.length]!;
  const end = k + 1 < keys.length ? b[0]! : 1 + b[0]!;
  const w = smooth((phase - a[0]!) / (end - a[0]!));
  for (let i = 1; i < a.length; i++) SWIM_SAMPLE[i] = a[i]! + (b[i]! - a[i]!) * w;
  return SWIM_SAMPLE;
}

function sampleStroke(keys: readonly SwimKey[], phase: number, side: number, pos: Vector3, palm: Vector3, fingers: Vector3): void {
  const s = sampleLoop(keys, phase);
  pos.set(side * s[1]!, s[2]!, s[3]!);
  palm.set(side * s[4]!, s[5]!, s[6]!);
  fingers.set(side * s[7]!, s[8]!, s[9]!);
}

/** Travel is measured in the support frame, never from world-root motion or smoothed speed. */
export interface AvatarLocomotion {
  distance: number;
  /** First sample, support-frame change, or teleport: discard old foot history. */
  discontinuous: boolean;
  /** Support-local to world; null denotes the stationary world frame. */
  frameToWorld: Matrix | null;
}

interface RemoteArm {
  limb: ArticulatedArm;
  shoulder: Vector3;
  targetShoulder: Vector3;
  shoulderOffset: Vector3;
  target: Vector3;
  contact: Vector3;
  pole: Vector3;
  offset: Vector3;
  rotation: Quaternion;
  targetRotation: Quaternion;
  rotationOffset: Quaternion;
  /** Fixed barrel surface anchor and hand basis for the manned cannon. */
  cannonContact: Vector3;
  cannonRotation: Quaternion;
}

/** Articulated crewmate: feet root stays on the physical ground/deck. */
export class Avatar {
  readonly root: TransformNode;
  readonly body: TransformNode;
  readonly carryPoint: TransformNode;
  readonly head: TransformNode;
  private readonly torso: TransformNode;
  private readonly meshes: Mesh[];
  private readonly env: Environment;
  private readonly legs: [ArticulatedLeg, ArticulatedLeg];
  private readonly arms: [RemoteArm, RemoteArm];
  private readonly sword: TransformNode;
  private readonly shovel: TransformNode;
  private readonly spyglass: TransformNode;
  private readonly pistol: TransformNode;
  /** Transient repair props: the hammer rides the right-hand grip, the plank the action frame. */
  private readonly hammer: TransformNode;
  private readonly plank: TransformNode;
  private readonly repairClock = new RepairClock();
  private readonly repairPose = new RepairPose();
  /** 0 … 1 re-draw of the carried tool after a repair, which would otherwise swing up through the view. */
  private repairRedraw = 1;
  private readonly pistolMechanism: PistolMechanism;
  /** Rack bucket (tools/models/bucket.py), posed in the body frame by `sampleBucketPoseToRef`. */
  private readonly bucket: TransformNode;
  private readonly bucketWater: TransformNode;
  private readonly bucketPose: BucketPose = { x: 0, y: 0, z: 0, yaw: 0, pitch: 0, roll: 0, hipDrop: 0, lean: 0, support: "free", water: false };
  /** Fill/throw progress advanced locally between server patches. */
  private bucketProgress = 0;
  private bucketAction = "";
  private readonly bucketPalm = new Vector3();
  private readonly bucketFingers = new Vector3();
  private readonly pistolPose = createPistolPose();
  private readonly local = new Vector3();
  private readonly hip = new Vector3();
  private readonly foot = new Vector3();
  private readonly airFoot = new Vector3();
  /** Generic-pose palm normal and finger direction in the action frame. */
  private readonly palm = new Vector3();
  private readonly fingers = new Vector3();
  private readonly handAxisX = new Vector3();
  private readonly handAxisY = new Vector3();
  private readonly handAxisZ = new Vector3();
  private readonly handBasis = new Matrix();
  private readonly pole = new Vector3();
  private readonly rotation = Quaternion.Identity();
  private readonly toolRotation = Quaternion.Identity();
  private readonly scratchRotation = Quaternion.Identity();
  private readonly identityRotation = Quaternion.Identity();
  private readonly scratch = new Vector3();
  /** The free hand pose a climber blends back to while stepping off the ladder. */
  private readonly ladderFreeHand = new Vector3();
  private readonly ladderFreeRotation = Quaternion.Identity();
  private readonly helm = new HelmHands();
  private readonly hangingRotation = Quaternion.RotationYawPitchRoll(0, -Math.PI / 2, 0);
  private readonly shovelRotation = Quaternion.RotationYawPitchRoll(0, 0, Math.PI / 2);
  private readonly shovelGripFlip = Quaternion.RotationYawPitchRoll(Math.PI, 0, 0);
  private readonly swordGripTwist = Quaternion.RotationYawPitchRoll(0, 0, Math.PI);
  private readonly eye: TransformNode;
  private readonly actionFrame: TransformNode;
  private readonly footFrameInverse = new Matrix();
  private readonly cannonInverse = new Matrix();
  private readonly cannonShoulder = new Vector3();
  private readonly swordPose: SwordPose = { contact: new Vector3(), yaw: 0, pitch: 0, roll: 0 };
  private readonly swordBody = createSwordBody();
  /** Cut lean added on top of the eased stance lean, removed before the next frame's easing. */
  private swordLean = 0;
  private readonly torsoTilt = new Matrix();
  private readonly footPlants = [new Vector3(), new Vector3()] as const;
  private readonly footCycles = [-1, -1];
  private readonly footContacts = [new Vector3(), new Vector3()] as const;
  private readonly footOffsets = [new Vector3(), new Vector3()] as const;
  private footTransition = 1;
  private lastFootMode = 0;
  private readonly diagnosticArms: readonly ArticulatedArm[];
  /** Advances π per footfall: one full cycle is a left and a right step. */
  private walkPhase = 0;
  /** Eases the stride's hip bounce in and out with walking. */
  private strideBlend = 0;
  /** Eased third-person carry: action-frame drop/draw-in, tool-hand turn and the share of look pitch it follows. */
  private carryY = 0;
  private carryZ = 0;
  private carryYaw = 0;
  private carryPitch = 1;
  /** Eased 0…1 per arm: the hand hangs free instead of holding its view pose. */
  private readonly freeWeights = [0, 0];
  private readonly freeHand = createFreeHand();
  private readonly viewLag = new ViewmodelLag();
  /** Camera position relative to the feet at the last first-person update, world axes. */
  private readonly lagCamera = new Vector3();
  private lagSeeded = false;
  private readonly lagInverse = new Matrix();
  private time = 0;
  private shadowOn = true;
  private initialized = false;
  private hipHeight = 0.82;
  /** Swim presentation, derived from rendered motion alone so remotes match the local rig. */
  private swimPhase = 0;
  private swimMove = 0;
  private swimUnder = 0;
  private swimPitch = 0;
  private swimRate = 0;
  private swimHorizontal = 0;
  private swimPrevY = Number.NaN;
  private readonly swimKnee = new Vector3();
  private readonly swimTread = new Vector3();
  private readonly swimTreadKnee = new Vector3();
  private readonly swimFootRotation = Quaternion.Identity();
  private readonly swimPalm = new Vector3();
  private readonly swimFingers = new Vector3();
  private readonly swimPosition = new Vector3();
  /** First-person camera, or null in third person. */
  private view: Camera | null = null;
  private readonly headMeshes: Mesh[];
  /** Arms, hands and held tools: drawn over nearby geometry in first person. */
  private readonly overlayMeshes: Mesh[];
  private readonly hiddenMasks = new Map<Mesh, number>();
  private overlayGroup = 0;
  private overlayHidden = false;
  /** Preset-only life (null on the classic crewmate): blinking lids, coat panels that follow the thighs. */
  private readonly eyelids: TransformNode | null;
  private readonly skirts: readonly [TransformNode, TransformNode] | null;
  private readonly hipJoints: readonly [TransformNode, TransformNode];
  private readonly kneeJoints: readonly [TransformNode, TransformNode];
  private blinkWait = 1 + Math.random() * 3;
  private blinkAge = -1;
  private readonly torsoInverse = new Matrix();
  private readonly hipLocal = new Vector3();
  private readonly kneeLocal = new Vector3();

  /** `look`: the customizable crewmate in that look; null keeps the classic crewmate. */
  constructor(scene: Scene, env: Environment, playerId: string, look: CharacterLook | null = null) {
    this.env = env;
    this.root = new TransformNode("avatar", scene);
    this.body = new TransformNode("avatarBody", scene); this.body.parent = this.root;
    const coat = characterCoatColor(playerId);
    const model = look ? spawnModel(scene, env.mats, "crewmate_custom", lookSpawnOptions(look, coat)) : spawnModel(scene, env.mats, "crewmate", { colors: { coat } });
    model.root.parent = this.body; this.torso = model.part("torso"); this.head = model.part("head");
    this.eye = new TransformNode("avatarEye", scene); this.eye.parent = this.torso; this.eye.position.set(0, 1.70, 0);
    this.actionFrame = new TransformNode("avatarActionFrame", scene); this.actionFrame.parent = this.torso;
    this.actionFrame.position.set(THIRD_PERSON_ACTION_FRAME.x, THIRD_PERSON_ACTION_FRAME.y, THIRD_PERSON_ACTION_FRAME.z);
    this.legs = [new ArticulatedLeg(model, "L"), new ArticulatedLeg(model, "R")];
    this.hipJoints = [model.part("legL"), model.part("legR")];
    this.kneeJoints = [model.part("kneeL"), model.part("kneeR")];
    this.eyelids = model.findPart("eyelids");
    const skirtL = model.findPart("skirtL"), skirtR = model.findPart("skirtR");
    this.skirts = skirtL && skirtR ? [skirtL, skirtR] : null;
    const makeArm = (suffix: "L" | "R"): RemoteArm => ({
      limb: new ArticulatedArm(model, suffix, "hanging"), shoulder: new Vector3(), targetShoulder: new Vector3(), shoulderOffset: new Vector3(), target: new Vector3(), contact: new Vector3(),
      pole: new Vector3(), offset: new Vector3(), rotation: Quaternion.Identity(), targetRotation: Quaternion.Identity(), rotationOffset: Quaternion.Identity(),
      cannonContact: new Vector3(), cannonRotation: Quaternion.Identity(),
    });
    this.arms = [makeArm("L"), makeArm("R")]; this.diagnosticArms = [this.arms[0].limb, this.arms[1].limb];
    const grip = new TransformNode("remoteGrip", scene); grip.parent = this.arms[1].limb.hand; grip.position.copyFrom(HANGING_HAND_GRIP);
    grip.rotation.x = Math.PI / 2;
    const sword = spawnModel(scene, env.mats, "sword"), shovel = spawnModel(scene, env.mats, "shovel"), spyglass = spawnModel(scene, env.mats, "spyglass");
    const pistol = spawnModel(scene, env.mats, "pistol");
    this.pistol = pistol.root; this.pistol.parent = grip; this.pistol.rotation.z = Math.PI / 2;
    this.pistolMechanism = new PistolMechanism(pistol);
    Vector3.TransformNormalToRef(this.pistolMechanism.rightGrip, Matrix.RotationZ(Math.PI / 2), this.pistol.position);
    this.pistol.position.scaleInPlace(-1);
    this.sword = sword.root; this.shovel = shovel.root; this.spyglass = spyglass.root;
    this.sword.parent = this.shovel.parent = grip; this.sword.rotationQuaternion = SWORD_GRIP_ROTATION.clone();
    this.shovel.rotationQuaternion = this.shovelGripFlip.conjugate().multiply(Quaternion.RotationYawPitchRoll(0, 0, -Math.PI / 2));
    this.spyglass.parent = this.torso; this.spyglass.rotationQuaternion = Quaternion.Identity();
    const hammer = spawnModel(scene, env.mats, "hammer"), plank = spawnModel(scene, env.mats, "plank");
    this.hammer = hammer.root; this.hammer.parent = grip; this.hammer.rotationQuaternion = HAMMER_GRIP_ROTATION.clone();
    this.plank = plank.root; this.plank.parent = this.actionFrame;
    this.hammer.setEnabled(false); this.plank.setEnabled(false);
    const bucket = spawnModel(scene, env.mats, "bucket", { materials: { spray: env.mats.get("water", { alpha: 0.8, emissive: 0.2 }) } });
    this.bucket = bucket.root; this.bucket.parent = this.body; this.bucket.rotationQuaternion = Quaternion.Identity();
    this.bucketWater = bucket.part("water");
    this.bucket.setEnabled(false);
    this.meshes = [...model.meshes, ...sword.meshes, ...shovel.meshes, ...spyglass.meshes, ...pistol.meshes, ...hammer.meshes, ...plank.meshes, ...bucket.meshes];
    for (const mesh of this.meshes) { mesh.isPickable = false; env.addCaster(mesh); }
    const within = (mesh: Mesh, parts: readonly Node[]): boolean => parts.some((part) => mesh === part || mesh.isDescendantOf(part));
    const head = [model.part("head")];
    const arms = (["L", "R"] as const).flatMap((s) => [model.part(`upperArm${s}`), model.part(`forearm${s}`), model.part(`hand${s}`)]);
    this.headMeshes = model.meshes.filter((mesh) => within(mesh, head));
    this.overlayMeshes = [...model.meshes.filter((mesh) => within(mesh, arms)), ...sword.meshes, ...shovel.meshes, ...spyglass.meshes, ...pistol.meshes, ...hammer.meshes, ...plank.meshes];
    this.carryPoint = new TransformNode("carry", scene); this.carryPoint.parent = this.torso; this.carryPoint.position.set(0, 0.95, 0.48);
  }

  diagnostics(): readonly ArticulatedArm[] { return this.diagnosticArms; }

  /** Root position/yaw use its parent coordinates; locomotion explicitly supplies the support frame. */
  set(x: number, y: number, z: number, yaw: number, locomotion: AvatarLocomotion, dt: number, a: CharacterPose, state: CharacterState): void {
    const launching = a.action === "launch", sliding = !launching && a.slideProgress >= 0;
    const swimming = a.action === "swim", carrying = a.action === "chest";
    // Surface swimmers ride the rendered waves; swim state below reads the physical height.
    this.root.position.set(x, swimming ? y + a.waterHeave : y, z);
    const firstPerson = this.view !== null;
    const cannon = !launching && (a.station.startsWith("cannon:") || a.station.startsWith("islandCannon:")) && a.cannon !== null && !a.cannon.isDisposed();
    // Third-person hull and gun repairs kneel low; the helm, rigging and canvas are worked standing.
    // First person keeps the camera-driven body.
    const repairing = a.action === "repair", kneeling = repairing && !firstPerson && !a.repairStanding;
    const bar = launching || a.capstan === null ? -1 : capstanBar(a.station), capstan = bar >= 0;
    const kick = a.action === "anchorKick";
    // Pawl kick: wind the right knee up, stamp forward through the lever, recover.
    const kickLift = kick ? Math.sin(Math.min(1, a.actionProgress / 0.4) * Math.PI / 2) * (1 - smooth((a.actionProgress - 0.45) / 0.5)) : 0;
    const kickThrust = kick ? smooth((a.actionProgress - 0.25) / 0.2) * (1 - smooth((a.actionProgress - 0.55) / 0.4)) : 0;
    const ladder = !launching && a.station === "ladder" && a.climbRoot !== null, wheel = !launching && a.station === "wheel" && a.wheel !== null;
    this.time += dt;
    // Cutlass cuts and struck flinches move the whole body, not just the blade hand.
    const sword = sampleSwordBodyToRef(a.attackStep, a.attackPhase, a.hitStep, a.hitFlinch, this.swordBody);
    const swordFree = launching || swimming || sliding || carrying || kick || repairing || a.station !== "" ? 0 : 1;
    const swordTorso = swordFree * (firstPerson ? FIRST_PERSON_SWORD_BODY : 1);
    const lunge = firstPerson ? 0 : sword.lunge * swordFree;
    const resetFeet = !this.initialized || locomotion.discontinuous;
    const travel = resetFeet ? 0 : locomotion.distance;
    const gait = gaitFor(a.crouched, a.sprinting, carrying), duty = gait.reach / gait.step;
    if (!sliding && !launching && dt > 0) this.walkPhase += Math.min(travel, Math.max(0.15, dt * 12)) * Math.PI / gait.step;
    this.body.rotation.set(0, sliding ? a.slideYaw : yaw, 0);
    if (swimming) this.updateSwim(y, travel, dt, resetFeet);
    else this.swimPrevY = Number.NaN;
    const swimLean = swimming ? this.swimLean() : 0;
    // Scooping bends the hips and torso; the throw leans into the swing. First person keeps
    // the camera-derived upright body and lifts the scoop into view instead.
    const bucketWork = a.equipment === "bucket" && (a.action === "fill" || a.action === "throw") && !firstPerson;
    if (a.equipment === "bucket") this.updateBucketPose(a, dt, firstPerson);
    const bucketHip = bucketWork ? this.bucketPose.hipDrop : 0, bucketLean = bucketWork ? this.bucketPose.lean : 0;
    // Swimmers keep the neck just under the eye (the camera in first person), whatever the lean.
    const targetHip = (launching ? 0.72 : swimming ? (firstPerson ? this.cameraHeight() : SWIM_EYE) - FIRST_PERSON_NECK_BELOW_EYE - HEAD_ABOVE_HIPS * Math.cos(swimLean)
      : sliding ? 0.38 : kneeling ? REPAIR_KNEEL.hip : a.crouched ? 0.15 : a.sprinting ? 0.75 : 0.78) - bucketHip;
    this.hipHeight += (targetHip - this.hipHeight) * (this.initialized ? 1 - Math.exp(-dt / 0.07) : 1);
    const targetLean = (launching ? 0 : swimming ? swimLean : sliding ? -0.55 : cannon ? (firstPerson ? FIRST_PERSON_CANNON_LEAN : 0.42) : wheel ? HELM_LEAN
      : capstan ? (firstPerson ? FIRST_PERSON_CAPSTAN_LEAN : CAPSTAN_LEAN) : kick ? -0.12 * kickLift : kneeling ? REPAIR_KNEEL.lean : a.crouched ? 0.25 : a.sprinting ? 0.12 : 0) + bucketLean;
    // Rotate about the hips, using the same easing as their height; the cut's lean rides on top.
    this.torso.rotation.x -= this.swordLean;
    this.torso.rotation.x += (targetLean - this.torso.rotation.x) * (this.initialized ? 1 - Math.exp(-dt / 0.07) : 1);
    this.swordLean = sword.lean * swordTorso;
    this.torso.rotation.x += this.swordLean;
    this.torso.rotation.y = sword.twist * swordTorso;
    this.torso.rotation.z = sword.roll * swordTorso;
    const lean = this.torso.rotation.x;
    // First person derives the hips from the camera's actual height, keeping the neck a fixed
    // drop below the eye through crouch, slide and their transitions (the camera eases on its
    // own schedule); the camera already carries the landing dip.
    const cameraHips = firstPerson && !cannon && !wheel && !swimming && !launching;
    if (cameraHips) {
      this.hipHeight = Math.max(FIRST_PERSON_MIN_HIP, Math.min(FIRST_PERSON_MAX_HIP,
        this.cameraHeight() - FIRST_PERSON_NECK_BELOW_EYE - HEAD_ABOVE_HIPS * Math.cos(lean)));
    }
    // Landing absorbs the impact in the knees: the hips dip and the legs bend.
    const landingDrop = !cameraHips && state.landing >= 0 ? 0.12 * state.landingImpact * Math.sin(state.landing * Math.PI) : 0;
    // Running hips sink over each planted foot and rise through the flight between steps.
    const striding = !cameraHips && !launching && !sliding && !swimming && travel > 0.001 && state.airBlend < 0.5;
    this.strideBlend += ((striding ? 1 : 0) - this.strideBlend) * (this.initialized ? 1 - Math.exp(-dt / 0.1) : 1);
    const stepPhase = this.walkPhase / Math.PI - duty;
    const hipY = this.hipHeight - landingDrop - (cameraHips ? 0 : sword.drop * swordFree)
      - Math.cos(stepPhase * Math.PI * 2) * gait.bounce * 0.5 * this.strideBlend;
    Matrix.RotationYawPitchRollToRef(this.torso.rotation.y, lean, this.torso.rotation.z, this.torsoTilt);
    Vector3.TransformNormalFromFloatsToRef(0, TORSO_HIP_PIVOT, 0, this.torsoTilt, this.scratch);
    this.torso.position.set(-this.scratch.x, hipY - this.scratch.y, lunge - this.scratch.z);
    this.head.rotation.x = swimming ? this.swimHeadPitch(lean, a.pitch) : a.pitch;
    this.head.rotation.y = sliding ? Math.max(-0.6, Math.min(0.6, Math.atan2(Math.sin(yaw - a.slideYaw), Math.cos(yaw - a.slideYaw)))) : 0;
    const scope = Math.max(0, Math.min(1, a.scopeBlend)), raised = scope * scope * (3 - 2 * scope);
    // Arm layers (character/arm-layers.ts): third-person tools ease between their view reach and a
    // resting carry; sprinting arms ignore the look entirely.
    const armEase = this.initialized ? 1 - Math.exp(-dt / 0.1) : 1;
    const carry = !firstPerson && state.toolsVisible ? carryWeight(a) : 0, carryOffset = THIRD_PERSON_CARRY[state.shownEquipment];
    this.carryY += (carryOffset.y * carry - this.carryY) * armEase;
    this.carryZ += (carryOffset.z * carry - this.carryZ) * armEase;
    this.carryYaw += (carryOffset.yaw * carry - this.carryYaw) * armEase;
    this.carryPitch += ((a.sprinting ? 0 : 1 - (1 - CARRY_PITCH_SHARE) * carry) - this.carryPitch) * armEase;
    if (firstPerson) {
      this.updateViewmodelLag(dt, resetFeet || a.reducedMotion);
      const [lagX, lagY, lagZ] = this.viewLag.offset;
      this.actionFrame.position.set(FIRST_PERSON_ACTION_FRAME.x + lagX, FIRST_PERSON_ACTION_FRAME.y + lagY, FIRST_PERSON_ACTION_FRAME.z + lagZ);
      // The camera frame already carries pitch, yaw and view swing; the tube is camera-local.
      // A raised eyepiece stays at the eye; the lowered tube trails with the hands.
      this.spyglass.rotationQuaternion!.copyFrom(this.identityRotation);
      this.spyglass.position.set(0.16 * (1 - raised), -0.14 * (1 - raised) - (a.sprinting ? 0.20 : 0), (a.sprinting ? 0.24 : 0.30) * (1 - raised) + 0.025 * raised);
      this.spyglass.position.addInPlaceFromFloats(lagX * (1 - raised), lagY * (1 - raised), lagZ * (1 - raised));
    } else {
      this.eye.rotation.set(a.pitch, this.head.rotation.y, 0);
      this.actionFrame.position.set(THIRD_PERSON_ACTION_FRAME.x, THIRD_PERSON_ACTION_FRAME.y + this.carryY, THIRD_PERSON_ACTION_FRAME.z + this.carryZ);
      this.actionFrame.rotation.set(a.pitch * this.carryPitch, this.head.rotation.y, 0);
      // The work stays low in front of the kneeling body wherever the head looks.
      if (kneeling) this.actionFrame.rotation.x = REPAIR_KNEEL.pitch;
      // Carry at chest height; only the raised eyepiece follows the eye origin.
      // Keeping the lowered tube at eye height overreaches the support arm when looking up.
      Quaternion.RotationYawPitchRollToRef(this.eye.rotation.y, this.actionFrame.rotation.x, 0, this.spyglass.rotationQuaternion!);
      this.spyglass.position.set(0.16 * (1 - raised), -0.14 * (1 - raised) - (a.sprinting ? 0.20 : 0), (a.sprinting ? 0.24 : 0.30) * (1 - raised) + 0.025 * raised);
      this.spyglass.position.rotateByQuaternionToRef(this.spyglass.rotationQuaternion!, this.spyglass.position);
      Vector3.LerpToRef(this.actionFrame.position, this.eye.position, raised, this.scratch);
      this.spyglass.position.addInPlace(this.scratch);
      // The tube is the held tool's frame here: it takes the carried-hand sway.
      this.spyglass.position.z += armSwing(this.walkPhase, 1) * gait.carrySway * this.strideBlend * (1 - raised);
      // The head turns and lifts against the cut so the gaze stays on the opponent.
      this.head.rotation.y -= 0.6 * this.torso.rotation.y;
      this.head.rotation.x -= 0.5 * this.swordLean;
    }
    if (cannon) {
      // The gunner's chest follows the barrel's grips (the cascabel, or the rear flare in
      // first person), keeping both palms in reach while the aim swings; the free look only moves the head.
      Vector3.TransformCoordinatesFromFloatsToRef(0, 0, firstPerson ? FIRST_PERSON_CANNON_GRIP.station : -0.35, a.cannon!.computeWorldMatrix(true), this.local);
      const cannonParent = this.root.parent as TransformNode | null;
      if (cannonParent) { cannonParent.computeWorldMatrix(true).invertToRef(this.footFrameInverse); Vector3.TransformCoordinatesToRef(this.local, this.footFrameInverse, this.local); }
      this.local.subtractToRef(this.root.position, this.local);
      this.body.rotation.y = Math.atan2(this.local.x, this.local.z);
    } else if (ladder || wheel) {
      const stationFrame = ladder ? a.climbRoot! : (a.wheel!.parent as TransformNode | null) ?? a.wheel!;
      Vector3.TransformNormalFromFloatsToRef(0, 0, 1, stationFrame.computeWorldMatrix(true), this.local);
      const parent = this.root.parent as TransformNode | null;
      if (parent) {
        parent.computeWorldMatrix(true).invertToRef(this.footFrameInverse);
        Vector3.TransformNormalToRef(this.local, this.footFrameInverse, this.local);
      }
      this.body.rotation.y = Math.atan2(this.local.x, this.local.z) - (ladder ? Math.PI / 2 : 0);
      if (wheel) {
        // Feet planted and hands on the wheel, the helmsman rolls and pitches with the hull rather
        // than standing world-upright, so a heeling deck never pulls the handles out of reach.
        Quaternion.FromRotationMatrixToRef(stationFrame.getWorldMatrix(), this.scratchRotation);
        if (parent) {
          Quaternion.FromRotationMatrixToRef(this.footFrameInverse, this.toolRotation);
          this.toolRotation.multiplyToRef(this.scratchRotation, this.scratchRotation);
        }
        this.scratchRotation.toEulerAnglesToRef(this.body.rotation);
      }
    } else if (capstan) {
      // Chest square to the bar: face along its sweep, the head on the right.
      Vector3.TransformNormalFromFloatsToRef(Math.cos(bar * Math.PI / 2), 0, Math.sin(bar * Math.PI / 2), a.capstan!.computeWorldMatrix(true), this.local);
      const parent = this.root.parent as TransformNode | null;
      if (parent) {
        parent.computeWorldMatrix(true).invertToRef(this.footFrameInverse);
        Vector3.TransformNormalToRef(this.local, this.footFrameInverse, this.local);
      }
      this.body.rotation.y = Math.atan2(this.local.z, -this.local.x);
    } else if (kick && a.capstan?.parent) {
      // Turn the hips to the deck pawl for the stamp, then back to the look direction.
      const ship = (a.capstan.parent as TransformNode).computeWorldMatrix(true);
      Vector3.TransformCoordinatesFromFloatsToRef(CAPSTAN.pawl.x, 0, CAPSTAN.pawl.z, ship, this.local);
      const parent = this.root.parent as TransformNode | null;
      if (parent) {
        parent.computeWorldMatrix(true).invertToRef(this.footFrameInverse);
        Vector3.TransformCoordinatesToRef(this.local, this.footFrameInverse, this.local);
      }
      this.local.subtractInPlace(this.root.position);
      const toPawl = Math.atan2(this.local.x, this.local.z) - yaw, turn = Math.atan2(Math.sin(toPawl), Math.cos(toPawl));
      this.body.rotation.y = yaw + turn * smooth(a.actionProgress / 0.2) * (1 - smooth((a.actionProgress - 0.75) / 0.25));
    }
    // First person keeps the neck under the eye: the feet sit slightly behind it,
    // further when looking down, and the torso's lean about the hips moves them back
    // by the head's forward travel so the collar and chest never rise into view.
    const leanReach = HEAD_ABOVE_HIPS * Math.sin(lean);
    const bodyBack = firstPerson
      ? FIRST_PERSON_BODY_BACK + FIRST_PERSON_LOOK_DOWN_BACK * Math.max(0, Math.sin(a.pitch)) + (cannon ? 0 : leanReach)
      : 0;
    this.body.position.set(-Math.sin(this.body.rotation.y) * bodyBack, 0, -Math.cos(this.body.rotation.y) * bodyBack);
    if (wheel) {
      // The helmsman stands on the ship's own helm point (the wheel's parent is the ship root),
      // stepped up to the wheel, rather than at sampled or predicted feet a few centimetres off
      // it; the same body in every view, with the first-person eye riding its head below.
      Vector3.TransformCoordinatesFromFloatsToRef(SHIP_POINTS.helm.x, SHIP_POINTS.helm.y, SHIP_POINTS.helm.z + HELM_STEP,
        a.wheel!.parent ? (a.wheel!.parent as TransformNode).getWorldMatrix() : Matrix.IdentityReadOnly, this.scratch);
      const parent = this.root.parent as TransformNode | null;
      if (parent) {
        parent.computeWorldMatrix(true).invertToRef(this.footFrameInverse);
        Vector3.TransformCoordinatesToRef(this.scratch, this.footFrameInverse, this.scratch);
      }
      this.scratch.subtractToRef(this.root.position, this.body.position);
    }
    if (firstPerson && !wheel) {
      // Measure from where the eye actually is: station view offsets (the ladder's
      // step back from the hull) and sway move the camera off the feet.
      // globalPosition is cached; the camera's world matrix resyncs to this frame's position.
      this.scratch.copyFrom(this.view!.computeWorldMatrix().getTranslation());
      const parent = this.root.parent as TransformNode | null;
      if (parent) {
        parent.computeWorldMatrix(true).invertToRef(this.footFrameInverse);
        Vector3.TransformCoordinatesToRef(this.scratch, this.footFrameInverse, this.scratch);
      }
      this.body.position.x += this.scratch.x - this.root.position.x;
      this.body.position.z += this.scratch.z - this.root.position.z;
    }
    if (firstPerson && cannon) {
      // The gunner leans over the breech to reach it; the eye rides the leaning head
      // instead, so both palms stay on the barrel and the view sights along it.
      Vector3.TransformNormalFromFloatsToRef(0, -HEAD_ABOVE_HIPS * (1 - Math.cos(lean)), leanReach, this.body.computeWorldMatrix(true), this.scratch);
      this.view!.position.addInPlace(this.scratch);
    }
    if (firstPerson && wheel) {
      // Move the eye's base (standing eye height above the feet) onto the helmsman's head,
      // keeping the view's decorative bob and shake on top.
      Vector3.TransformCoordinatesFromFloatsToRef(0, FIRST_PERSON_HELM_EYE.y, FIRST_PERSON_HELM_EYE.z, this.torso.computeWorldMatrix(true), this.scratch);
      this.scratch.subtractInPlace(this.root.getAbsolutePosition());
      this.scratch.y -= PLAYER.eyeHeight;
      this.view!.position.addInPlace(this.scratch);
    }
    const tools = state.toolsVisible, shown = state.shownEquipment, changed = state.changed;
    this.sword.setEnabled(tools && shown === "sword"); this.shovel.setEnabled(tools && shown === "shovel"); this.spyglass.setEnabled(tools && shown === "spyglass");
    this.pistol.setEnabled(tools && shown === "pistol");
    this.hammer.setEnabled(repairing); this.plank.setEnabled(repairing);
    const repairEnded = !repairing && this.repairClock.value > 0;
    this.repairRedraw = repairEnded ? 0 : Math.min(1, this.repairRedraw + dt / CHARACTER_TIMING.equipSeconds);
    const repair = this.repairClock.advance(repairing, a.actionProgress, dt);
    if (repairing) this.plank.position.copyFrom(this.repairPose.sample(repair, firstPerson ? FIRST_PERSON_REPAIR_PLANK : REPAIR_PLANK).plank);
    const bucket = tools && shown === "bucket";
    this.bucket.setEnabled(bucket);
    if (bucket) {
      const pose = this.bucketPose;
      this.bucket.position.set(pose.x, pose.y, pose.z);
      Quaternion.RotationYawPitchRollToRef(pose.yaw, pose.pitch, pose.roll, this.bucket.rotationQuaternion!);
      this.bucketWater.setEnabled(pose.water);
    }
    if (firstPerson) {
      // A raised eyepiece sits at the eye; hide arms and tube rather than fill the lens view.
      const hidden = a.scopeBlend >= 0.999;
      if (hidden !== this.overlayHidden) {
        this.overlayHidden = hidden;
        for (const mesh of this.overlayMeshes) { if (hidden) this.hide(mesh); else this.show(mesh); }
      }
      // World-gripping hands (stations, the carried chest) share depth with the world;
      // free arms draw over nearby geometry.
      this.setOverlayGroup(ladder || wheel || cannon || capstan || carrying || bucket ? 0 : 1);
    }
    // The left hand joins the objective end only as the spyglass rises.
    const spyglassSupport = tools && a.equipment === "spyglass" && !a.sprinting ? raised : 0;
    sampleSwordPoseToRef(a.attackStep, a.attackPhase, this.swordPose);
    samplePistolPoseToRef(a.pistolRounds, a.pistolReserve, a.pistolAimBlend, a.pistolReloadProgress, a.pistolRecoil, this.pistolPose, this.pistolMechanism.aimGripHeight);
    this.pistolMechanism.apply(this.pistolPose, dt);
    const eyeWorld = this.actionFrame.computeWorldMatrix(true);
    Quaternion.FromRotationMatrixToRef(eyeWorld, this.rotation);
    // Both palms slide on the cascabel toward the gunner's shoulders, so they stay
    // on the barrel as the aim swings its rear across the station.
    let cannonMidAzimuth = 0;
    if (cannon) {
      a.cannon!.computeWorldMatrix(true).invertToRef(this.cannonInverse);
      const torsoWorld = this.torso.computeWorldMatrix(true);
      for (let i = 0; i < 2; i++) {
        Vector3.TransformCoordinatesFromFloatsToRef(i === 0 ? -0.30 : 0.30, 1.40, 0, torsoWorld, this.cannonShoulder);
        Vector3.TransformCoordinatesToRef(this.cannonShoulder, this.cannonInverse, this.scratch);
        cannonMidAzimuth += Math.atan2(this.scratch.x, this.scratch.y) / 2;
      }
    }
    if (wheel) this.helm.update(a.wheel!.rotation.z, dt);
    else this.helm.reset();
    const bodyWorld = this.body.computeWorldMatrix(true);
    // Hands that hold nothing hang free in third person; first person keeps its viewmodel.
    const armsFree = !firstPerson && !(launching || repairing || a.action === "recover" || sliding || swimming || carrying || capstan || ladder || wheel || cannon || a.sprinting);
    for (let i = 0; i < 2; i++) {
      const arm = this.arms[i]!, side = i === 0 ? -1 : 1;
      Vector3.TransformCoordinatesFromFloatsToRef(side * 0.30, 1.40, 0, this.torso.computeWorldMatrix(true), arm.targetShoulder);
      Vector3.TransformCoordinatesFromFloatsToRef(side * 0.60, 1.05, -0.15, this.torso.computeWorldMatrix(true), arm.pole);
      this.local.set(side * 0.27, -0.23, 0.52);
      let handPose: HandPose = i === 1 && tools ? "tool" : "relaxed";
      // Shovel wrists wrap the shaft; transport their frame onto the otherwise un-authored forearm.
      arm.limb.forearmPronation = tools && a.equipment === "shovel" && !a.sprinting;
      // Euler poses are authored for the right hand; `handRotationToRef` mirrors them for the left.
      // `mirror` is cleared only by poses solved directly for this hand's world palm/fingers.
      let hp = 0.3, hy = -0.2, hr = -Math.PI / 2, mirror = true;
      const working = a.action === "repair" || a.action === "recover";
      const sprintFist = a.sprinting && !(tools && i === 1);
      // Generic poses state where the palm and fingers point in the action frame (per side,
      // so already correct for either hand); the authored tool grips keep tuned Euler angles.
      const facing = launching || working || sliding || sprintFist;
      if (launching) {
        this.local.set(side * 0.42, -0.18, 0.62); handPose = "open";
        this.palm.set(side * 0.39, -0.85, 0.35); this.fingers.set(-side * 0.29, 0.25, 0.93);
      } else if (repairing) {
        // Left palm braces the plank over the hole; the right hand drives the hammer onto its nail.
        if (i === 1) {
          this.local.copyFrom(this.repairPose.grip); this.palm.copyFrom(this.repairPose.palm); this.fingers.copyFrom(this.repairPose.fingers);
          handPose = "tool"; arm.limb.forearmPronation = true;
        } else {
          this.local.copyFrom(this.repairPose.brace); this.palm.copyFrom(REPAIR_BRACE_PALM); this.fingers.copyFrom(REPAIR_BRACE_FINGERS); handPose = "open";
        }
      } else if (working) {
        const work = Math.sin(a.actionProgress * Math.PI * 4 + i * Math.PI);
        this.local.set(side * 0.21, -0.18 + work * 0.06, 0.48 + work * 0.10); handPose = work > 0 ? "support" : "open";
        // Palms lie flat on the work, fingers pointing into it, and tip down to press.
        this.palm.set(-side * 0.2, -0.95, 0.25 - work * 0.25); this.fingers.set(side * 0.15, 0.25, 0.95);
      } else if (sliding) {
        this.local.set(side * 0.32, -0.40, 0.39); handPose = "open";
        this.palm.set(side * 0.15, -1, 0.1); this.fingers.set(side * 0.35, 0, 1);
      } else if (a.sprinting) {
        // Each arm drives with the opposite leg, once per stride: forward and up, back and down.
        const forward = strideOffset(this.walkPhase / (Math.PI * 2) + (i === 0 ? 0.5 : 0), duty);
        this.local.set(side * 0.28, -0.48 + forward * 0.08, 0.29 + forward * gait.armSwing);
        if (sprintFist) {
          // Running fists: palms face the body's centre line, knuckles forward.
          this.palm.set(-side, 0, 0); this.fingers.set(0, -0.3, 0.95);
        } else hp = -0.8;
      } else if (a.equipment === "pistol") {
        if (i === 1) {
          this.local.copyFrom(this.pistolPose.contact);
          hy = this.pistolPose.yaw; hp = this.pistolPose.pitch; hr = -Math.PI / 2 + this.pistolPose.roll;
        } else handPose = "support";
      } else if (a.equipment === "sword") {
        if (i === 1) { this.local.copyFrom(this.swordPose.contact); hy = this.swordPose.yaw; hp = this.swordPose.pitch; hr = this.swordPose.roll; this.local.z -= a.recoil; }
        else {
          this.local.set(side * SWORD_OFFHAND.x + sword.offX * swordTorso, SWORD_OFFHAND.y + sword.offY * swordTorso, SWORD_OFFHAND.z + sword.offZ * swordTorso);
          hy = SWORD_OFFHAND.yaw; hp = SWORD_OFFHAND.pitch; hr = SWORD_OFFHAND.roll;
        }
        if (a.staggerRemaining > 0) { this.local.y -= 0.18; hp += 0.5; }
        if (a.blocking || a.parryRaise > 0) {
          const guard = i === 1 ? SWORD_GUARD.sword : SWORD_GUARD.offhand;
          this.local.set(guard.x, guard.y + a.parryRaise * 0.06, guard.z - a.recoil);
          hy = guard.yaw; hp = guard.pitch; hr = guard.roll; mirror = false; handPose = i === 1 ? "tool" : "open";
        }
      } else if (a.equipment === "shovel") {
        const dig = a.action === "dig" ? digSwing(a.actionProgress) : 0;
        this.local.set(0.27, -0.14 - dig * 0.12, 0.36 + dig * 0.08); hp = Math.PI / 2 - 0.23 - dig * 0.55; hy = -2.45; hr = 0; handPose = "tool";
        // The shoulder carry runs the shaft back past the eye; in first person it swings across the body.
        if (firstPerson) hy += FIRST_PERSON_SHOVEL_YAW;
      }
      if (!launching && !swimming && !ladder && !wheel && !cannon && !capstan && !carrying && !a.reducedMotion) this.local.y += 0.04 * state.airBlend;
      if (tools) this.local.y -= Math.sin(state.equipmentBlend * Math.PI) * 0.2;
      if (tools && this.repairRedraw < 1) this.local.y -= Math.sin(this.repairRedraw * Math.PI) * 0.2;
      Vector3.TransformCoordinatesToRef(this.local, eyeWorld, arm.target);
      if (facing) this.handFacingToRef(this.palm, this.fingers, this.toolRotation);
      else {
        // Hanging authored palms use a different canonical axis than near hands.
        handRotationToRef(mirror ? side : 1, hy, hp, hr, this.toolRotation);
        // Only the right hand holds the shaft here; the left grip is solved from the shovel after, or stays free while sprinting.
        if (tools && a.equipment === "shovel" && i === 1) {
          this.toolRotation.multiplyToRef(this.shovelRotation, this.toolRotation);
          this.toolRotation.multiplyToRef(this.shovelGripFlip, this.toolRotation);
        }
        if (tools && a.equipment === "sword" && i === 1) {
          this.toolRotation.multiplyToRef(this.swordGripTwist, this.toolRotation);
          // Authored slash rotations need axial forearm pronation after correcting grip polarity.
          arm.limb.forearmPronation = true;
        }
        this.toolRotation.multiplyToRef(this.hangingRotation, this.toolRotation);
      }
      this.rotation.multiplyToRef(this.toolRotation, arm.targetRotation);
      if (i === 1 && tools && this.carryYaw !== 0) {
        // The carry turn pivots the tool hand about the vertical through its grip.
        Quaternion.RotationYawPitchRollToRef(this.carryYaw, 0, 0, this.scratchRotation);
        this.scratchRotation.multiplyToRef(arm.targetRotation, arm.targetRotation);
      }
      // Free: no tools shown, the lowered spyglass's or the bucket's left hand, or the resting cutlass offhand.
      const freeTarget = !armsFree ? 0 : !tools ? 1 : i === 1 ? 0
        : a.equipment === "sword" ? carry : a.equipment === "spyglass" ? 1 - spyglassSupport : a.equipment === "bucket" ? 1 : 0;
      const free = this.freeWeights[i]! += (freeTarget - this.freeWeights[i]!) * armEase;
      if (armsFree) {
        // Both layers run on the legs' gait clock: held hands sway, free hands swing with the opposite leg.
        const swing = armSwing(this.walkPhase, i) * this.strideBlend;
        if (tools && free < 1) {
          Vector3.TransformNormalFromFloatsToRef(0, 0, swing * gait.carrySway * (1 - free), bodyWorld, this.scratch);
          arm.target.addInPlace(this.scratch);
        }
        if (free > 0) {
          const hang = freeHandToRef(side, swing * gait.armSwing, a.crouched, this.freeHand);
          Vector3.TransformNormalFromFloatsToRef(hang.x, hang.y, hang.z, bodyWorld, this.scratch);
          Vector3.LerpToRef(arm.target, this.scratch.addInPlace(arm.targetShoulder), free, arm.target);
          Vector3.TransformNormalFromFloatsToRef(hang.poleX, hang.poleY, hang.poleZ, bodyWorld, this.scratch);
          Vector3.LerpToRef(arm.pole, this.scratch.addInPlace(arm.targetShoulder), free, arm.pole);
          Vector3.TransformNormalFromFloatsToRef(hang.palmX, hang.palmY, hang.palmZ, bodyWorld, this.palm);
          Vector3.TransformNormalFromFloatsToRef(hang.fingersX, hang.fingersY, hang.fingersZ, bodyWorld, this.fingers);
          this.handFacingToRef(this.palm, this.fingers, this.toolRotation);
          Quaternion.SlerpToRef(arm.targetRotation, this.toolRotation, free, arm.targetRotation);
          if (free > 0.5) handPose = "relaxed";
        }
      }
      if (swimming) handPose = this.swimArmToRef(arm, side);
      else if (carrying) {
        chestGripToRef(this.carryPoint, side, arm.target, true); handPose = "support";
        // Fingers follow the lowered forearms while palms face the side rings.
        handRotationToRef(side, 0.35, 0.7, -Math.PI / 2, this.toolRotation);
        this.toolRotation.multiplyToRef(this.hangingRotation, this.toolRotation);
        this.carryPoint.absoluteRotationQuaternion.multiplyToRef(this.toolRotation, arm.targetRotation);
      } else if (capstan) {
        capstanGripToRef(a.capstan!, bar, side, arm.target);
        // Palms ride the top of the bar and press forward; fingers wrap over its leading face.
        Vector3.TransformNormalFromFloatsToRef(0, -0.85, 0.55, bodyWorld, this.palm);
        Vector3.TransformNormalFromFloatsToRef(-side * 0.15, 0.1, 1, bodyWorld, this.fingers);
        this.handFacingToRef(this.palm, this.fingers, arm.targetRotation);
        // Elbows bend outboard and down, like a sailor driving the bar with straight backs.
        Vector3.TransformNormalFromFloatsToRef(side * 0.45, -0.4, -0.1, bodyWorld, this.local);
        arm.targetShoulder.addToRef(this.local, arm.pole);
        handPose = "rim";
      } else if (ladder || wheel) {
        // Stepping aboard past the handholds, or with nothing left in reach, the hands let go rather than stretch.
        const release = ladder ? ladderGripRelease(a.climbProgress, a.climbSpec) : 0;
        if (ladder) {
          // Shoulders stay on the torso, which hangs below the grips on the climb path, so the
          // arms never part from the body. The free pose solved above is where hands return.
          this.ladderFreeHand.copyFrom(arm.target); this.ladderFreeRotation.copyFrom(arm.targetRotation);
          ladderGripToRef(a.climbRoot!, a.climbProgress, side, arm.target, a.climbSpec);
          Quaternion.RotationYawPitchRollToRef(-Math.PI / 2, -0.25, 0, this.toolRotation);
          this.toolRotation.multiplyToRef(this.hangingRotation, this.toolRotation);
          a.climbRoot!.absoluteRotationQuaternion.multiplyToRef(this.toolRotation, arm.targetRotation);
          if (release > 0) {
            Vector3.LerpToRef(arm.target, this.ladderFreeHand, release, arm.target);
            Quaternion.SlerpToRef(arm.targetRotation, this.ladderFreeRotation, release, arm.targetRotation);
          }
          Vector3.TransformNormalFromFloatsToRef(0.35, -0.35, side * 0.15, a.climbRoot!.getWorldMatrix(), this.local);
        } else {
          // Shoulders stay on the torso (the lean and HELM_STEP bring the handles into reach);
          // the palms hold spoke handles and re-grip hand over hand as the wheel turns.
          this.helm.gripToRef(a.wheel!, i, arm.target, arm.targetRotation);
          Vector3.TransformNormalFromFloatsToRef(side * 0.4, -0.35, -0.1, this.body.computeWorldMatrix(true), this.local);
        }
        arm.targetShoulder.addToRef(this.local, arm.pole);
        handPose = "rim";
        if (ladder) {
          if (ladderHandReaching(a.climbProgress, side, a.climbSpec)) handPose = "open";
          else if (release > 0.5) handPose = "relaxed";
        }
        else if (this.helm.reaching(i)) handPose = "open";
      } else if (cannon) {
        // Anchored to the animated barrel itself: the palms stay on it while it yaws
        // or elevates, instead of floating in the eye frame.
        const cannonWorld = a.cannon!.computeWorldMatrix(true);
        if (firstPerson) {
          // The eye sights over the breech, so first-person palms brace the visible
          // rear flare, fingers turned diagonally forward, rather than the cascabel below.
          cannonGripToRef(side, FIRST_PERSON_CANNON_GRIP.azimuth,
            FIRST_PERSON_CANNON_GRIP.station - a.cannonRecoil * FIRST_PERSON_CANNON_GRIP.recoilTravel,
            arm.cannonContact, arm.cannonRotation);
          arm.cannonRotation.multiplyToRef(FIRST_PERSON_CANNON_WRISTS[i]!, arm.cannonRotation);
        } else {
          const physical = Math.max(-CANNON_HAND_MAX_AZIMUTH,
            Math.min(CANNON_HAND_MAX_AZIMUTH, cannonMidAzimuth + (side > 0 ? CANNON_HAND_SPREAD : -CANNON_HAND_SPREAD)));
          // Recoil slides the grip back along the cascabel's surface, never off it.
          cannonGripToRef(side, side * physical,
            REMOTE_CANNON_STATION - a.cannonRecoil * REMOTE_CANNON_RECOIL_TRAVEL,
            arm.cannonContact, arm.cannonRotation);
        }
        Vector3.TransformCoordinatesToRef(arm.cannonContact, cannonWorld, arm.target);
        a.cannon!.absoluteRotationQuaternion.multiplyToRef(arm.cannonRotation, this.toolRotation);
        this.toolRotation.multiplyToRef(this.hangingRotation, arm.targetRotation);
        handPose = "barrel";
        arm.limb.forearmPronation = true;
      } else if (tools && a.equipment === "spyglass" && (i === 1 || spyglassSupport > 0)) {
        spyglassGripToRef(this.spyglass, side, raised, this.scratch, this.toolRotation);
        this.toolRotation.multiplyToRef(this.hangingRotation, this.toolRotation);
        const weight = i === 1 ? 1 : spyglassSupport;
        Vector3.LerpToRef(arm.target, this.scratch, weight, arm.target);
        Quaternion.SlerpToRef(arm.targetRotation, this.toolRotation, weight, arm.targetRotation);
        if (weight > 0.5) handPose = "barrel";
        // Carry the grip's roll through the forearm rather than twisting the wrist seam.
        arm.limb.forearmPronation = true;
        // Raised hands sit at the face, where the default pole lies on the shoulder–wrist line;
        // aim the elbow out and forward of the shoulder instead.
        Vector3.TransformCoordinatesFromFloatsToRef(side * 0.70, 0.90, 0.30, this.torso.computeWorldMatrix(true), this.scratch);
        Vector3.LerpToRef(arm.pole, this.scratch, raised, arm.pole);
      }
      if (bucket && (i === 1 || this.bucketPose.support !== "free")) {
        this.bucketGripToRef(i === 1 ? "handle" : this.bucketPose.support as "rim" | "base", arm.target, arm.targetRotation);
        handPose = i === 1 ? "tool" : "support";
      }
      arm.limb.setDetail(a.detail);
      if (!(tools && a.equipment === "pistol" && (i === 1 || !a.sprinting))) arm.limb.setHandPose(state.release ? "open" : handPose, dt);
    }
    this.blendArm(this.arms[1], changed, state.transition);
    // Cutlass grips need the elbow behind the wrist throughout the authored swing.
    // Spyglass arms keep the default below-and-outboard elbow: grip-directed poles fold it into the chest.
    const rightWrapGrip = cannon || (tools && ((a.equipment === "shovel" && !a.sprinting) || a.equipment === "sword" || (a.equipment === "pistol" && !a.sprinting)));
    if (rightWrapGrip) {
      this.arms[1].limb.gripPoleToRef(this.arms[1].contact, this.arms[1].rotation, this.local);
      // Retain some outward elbow separation for the two closely spaced pistol grips, and for
      // first-person gunners whose forearms point straight along a yawed barrel.
      Vector3.LerpToRef(this.arms[1].pole, this.local, (tools && a.equipment === "pistol") ? 0.8 : cannon && firstPerson ? FIRST_PERSON_CANNON_GRIP_POLE : 1, this.arms[1].pole);
    }
    this.arms[1].limb.applyWorld(this.arms[1].shoulder, this.arms[1].contact, this.arms[1].pole, this.arms[1].rotation, dt, rightWrapGrip);
    if (tools && a.equipment === "shovel" && !a.sprinting) {
      Vector3.TransformCoordinatesFromFloatsToRef(0, 0.517, 0, this.shovel.computeWorldMatrix(true), this.arms[0].target);
      this.shovel.absoluteRotationQuaternion.multiplyToRef(this.shovelGripFlip, this.toolRotation);
      this.toolRotation.multiplyToRef(this.hangingRotation, this.arms[0].targetRotation);
    }
    if (tools && a.equipment === "pistol" && !a.sprinting) {
      this.pistolMechanism.supportToRef(this.pistolPose, this.arms[0].target);
      this.pistolMechanism.supportRotationToRef(this.pistolPose, this.toolRotation);
      this.toolRotation.multiplyToRef(this.hangingRotation, this.arms[0].targetRotation);
      // The reversed speedloader grip otherwise adds over 120 degrees of arbitrary axial roll.
      this.arms[0].limb.forearmPronation = a.pistolReloadProgress >= 0.46 && a.pistolReloadProgress <= 0.86;
    }
    this.blendArm(this.arms[0], changed, state.transition);
    if (tools && !a.sprinting && (a.equipment === "shovel" || a.equipment === "pistol")) {
      this.arms[0].limb.gripPoleToRef(this.arms[0].contact, this.arms[0].rotation, this.local);
      Vector3.LerpToRef(this.arms[0].pole, this.local, a.equipment === "pistol" ? 0.8 : 1, this.arms[0].pole);
    }
    if (cannon) {
      this.arms[0].limb.gripPoleToRef(this.arms[0].contact, this.arms[0].rotation, this.local);
      Vector3.LerpToRef(this.arms[0].pole, this.local, firstPerson ? FIRST_PERSON_CANNON_GRIP_POLE : 1, this.arms[0].pole);
    }
    const leftWrapGrip = cannon || (tools && !a.sprinting && (a.equipment === "shovel" || a.equipment === "pistol"));
    this.arms[0].limb.applyWorld(this.arms[0].shoulder, this.arms[0].contact, this.arms[0].pole, this.arms[0].rotation, dt, leftWrapGrip);
    if (tools && a.equipment === "pistol") {
      this.pistolMechanism.indexToRef(this.pistolPose, this.local);
      this.arms[1].limb.setPistolGrip(this.local, dt);
      if (!a.sprinting) this.arms[0].limb.setPistolSupport(this.pistolPose.supportProximal, this.pistolPose.supportDistal, this.pistolPose.supportThumb);
    }
    Quaternion.FromRotationMatrixToRef(bodyWorld, this.rotation);
    const feetFrame = locomotion.frameToWorld;
    if (feetFrame) feetFrame.invertToRef(this.footFrameInverse);
    const walking = !launching && travel > 0.001;
    const free = !launching && !sliding && !swimming && !ladder;
    const airborne = free && state.airBlend >= 0.5;
    const footMode = (walking ? 1 : 0) | (swimming ? 2 : 0) | (sliding ? 4 : 0) | (ladder ? 8 : 0) | (a.crouched ? 16 : 0) | (a.sprinting ? 32 : 0) | (launching ? 64 : 0) | (airborne ? 128 : 0) | (kick ? 256 : 0);
    const feetChanged = !resetFeet && footMode !== this.lastFootMode;
    this.footTransition = resetFeet ? 1 : feetChanged ? 0 : Math.min(1, this.footTransition + dt / 0.12);
    if (resetFeet) this.footCycles[0] = this.footCycles[1] = -1;
    this.lastFootMode = footMode;
    for (let i = 0; i < 2; i++) {
      const side = i === 0 ? -1 : 1, cycle = this.walkPhase / (Math.PI * 2) + i * 0.5;
      const fraction = cycle - Math.floor(cycle), reach = gait.reach;
      this.local.set(side * 0.115, 0.08, 0);
      if (launching) { this.local.z = -0.16; this.local.y = 0.30; }
      else if (sliding) {
        // Feet reach forward of hips that sit as low as the slide eye (first person) demands.
        const reach = Math.sqrt(Math.max(0, 0.64 - (hipY - 0.1) * (hipY - 0.1)));
        this.local.z = i === 0 ? Math.max(0.54, reach) : Math.max(-0.12, reach - 0.45); this.local.y = i === 0 ? 0.10 : 0.08;
      }
      else if (swimming) this.swimFootToRef(i, side, hipY, lean);
      else if (ladder) {
        // Feet step a rung behind the hands' cycle, so a climber hanging still keeps them planted.
        const rungPhase = ladderGripSlot(a.climbProgress, -1, a.climbSpec) * Math.PI * 2;
        this.local.z = Math.sin(rungPhase + i * Math.PI) * 0.25; this.local.y += 0.25;
      }
      else if (travel > 0.001 && fraction >= duty) {
        const t = (fraction - duty) / (1 - duty); this.local.z = strideOffset(cycle, duty) * reach; this.local.y += Math.sin(t * Math.PI) * gait.lift;
      } else if (kick && i === 1) { this.local.z = 0.06 + 0.5 * kickThrust; this.local.y += 0.4 * kickLift * (1 - 0.65 * kickThrust); }
      else if (travel <= 0.001) {
        // Standing cuts step the lead (left) or rear (right) foot, lifting it mid-step.
        this.local.z = (i === 0 ? sword.leadFoot : sword.rearFoot) * swordFree;
        this.local.y += (i === 0 ? sword.leadLift : sword.rearLift) * swordFree;
      }
      if (free && state.airBlend > 0) {
        // Rising tucks the knees; falling reaches the feet down for the deck.
        if (state.airborne === "rising") this.airFoot.set(side * 0.115, 0.32, i === 0 ? 0.12 : -0.06);
        else this.airFoot.set(side * 0.14, 0.14, i === 0 ? 0.10 : -0.10);
        Vector3.LerpToRef(this.local, this.airFoot, state.airBlend, this.local);
      }
      Vector3.TransformCoordinatesToRef(this.local, bodyWorld, this.foot);
      if (free && state.airBlend < 0.5 && travel > 0.001 && fraction < duty) {
        // A frame landing mid-stance plants the foot where it already is in the stride, not at its front.
        if (this.footCycles[i] !== Math.floor(cycle)) {
          this.footCycles[i] = Math.floor(cycle); this.local.set(side * 0.115, 0.08, strideOffset(cycle, duty) * reach);
          Vector3.TransformCoordinatesToRef(this.local, bodyWorld, this.footPlants[i]!);
          if (feetFrame) Vector3.TransformCoordinatesToRef(this.footPlants[i]!, this.footFrameInverse, this.footPlants[i]!);
        }
        if (feetFrame) Vector3.TransformCoordinatesToRef(this.footPlants[i]!, feetFrame, this.foot);
        else this.foot.copyFrom(this.footPlants[i]!);
      } else this.footCycles[i] = -1;
      if (resetFeet) this.footOffsets[i]!.setAll(0);
      else if (feetChanged) {
        this.rotation.conjugateToRef(this.scratchRotation);
        if (feetFrame) Vector3.TransformCoordinatesToRef(this.footContacts[i]!, feetFrame, this.scratch);
        else this.scratch.copyFrom(this.footContacts[i]!);
        this.scratch.subtractInPlace(this.foot);
        this.scratch.rotateByQuaternionToRef(this.scratchRotation, this.footOffsets[i]!);
      }
      this.footOffsets[i]!.rotateByQuaternionToRef(this.rotation, this.scratch);
      this.scratch.scaleInPlace(1 - smooth(this.footTransition));
      this.foot.addInPlace(this.scratch);
      this.footContacts[i]!.copyFrom(this.foot);
      if (feetFrame) Vector3.TransformCoordinatesToRef(this.footContacts[i]!, this.footFrameInverse, this.footContacts[i]!);
      if (resetFeet) this.footPlants[i]!.copyFrom(this.footContacts[i]!);
      // Hip sockets ride the torso's twist, bend and lunge; knees and feet stay square to the body.
      Vector3.TransformNormalFromFloatsToRef(side * HIP_HALF_WIDTH, 0, 0, this.torsoTilt, this.scratch);
      this.hip.set(this.scratch.x, hipY + this.scratch.y, lunge + this.scratch.z);
      Vector3.TransformCoordinatesToRef(this.hip, bodyWorld, this.hip);
      // Sliding knees bend up, not down into the deck.
      if (swimming) Vector3.TransformCoordinatesToRef(this.swimKnee, bodyWorld, this.pole);
      else Vector3.TransformCoordinatesFromFloatsToRef(side * HIP_HALF_WIDTH, sliding ? hipY + 0.4 : hipY - 0.2, 0.5 + lunge, bodyWorld, this.pole);
      this.legs[i]!.applyWorld(this.hip, this.foot, this.pole, swimming ? this.swimFootRotation : this.rotation);
    }
    this.animateLife(dt);
    this.initialized = true;
  }

  /** Blinks on a loose random rhythm; coat panels swing with the thighs, measured in the torso frame. */
  private animateLife(dt: number): void {
    if (this.eyelids) {
      this.blinkWait -= dt;
      if (this.blinkWait <= 0) {
        this.blinkAge = 0;
        this.blinkWait = 2.2 + Math.random() * 3.8;
      }
      let closed = 0;
      if (this.blinkAge >= 0) {
        this.blinkAge += dt;
        if (this.blinkAge >= BLINK_SECONDS) this.blinkAge = -1;
        else closed = Math.sin(this.blinkAge / BLINK_SECONDS * Math.PI);
      }
      this.eyelids.rotation.x = closed * EYELID_CLOSED;
    }
    if (!this.skirts) return;
    this.torso.computeWorldMatrix(true).invertToRef(this.torsoInverse);
    for (let i = 0; i < 2; i++) {
      Vector3.TransformCoordinatesToRef(this.hipJoints[i]!.computeWorldMatrix(true).getTranslation(), this.torsoInverse, this.hipLocal);
      Vector3.TransformCoordinatesToRef(this.kneeJoints[i]!.computeWorldMatrix(true).getTranslation(), this.torsoInverse, this.kneeLocal);
      this.kneeLocal.subtractInPlace(this.hipLocal);
      // Positive swing = knee ahead of the hip; a negative X turn carries the hem forward.
      const swing = Math.atan2(this.kneeLocal.z, -this.kneeLocal.y) * SKIRT_FOLLOW;
      this.skirts[i]!.rotation.x = -Math.max(-SKIRT_MAX_BACK, Math.min(SKIRT_MAX_FORWARD, swing));
    }
  }

  /** Samples the held bucket, advancing fill/throw progress smoothly between server patches. */
  private updateBucketPose(a: CharacterPose, dt: number, firstPerson: boolean): void {
    const action = a.action === "fill" || a.action === "throw" ? a.action : "";
    if (action !== this.bucketAction) {
      this.bucketAction = action;
      this.bucketProgress = a.actionProgress;
    } else if (action) {
      const predicted = this.bucketProgress + dt / (action === "fill" ? SHIP.bucketFillSeconds : SHIP.bucketThrowSeconds);
      this.bucketProgress = Math.min(1, Math.max(a.actionProgress - 0.15, Math.min(a.actionProgress + 0.15, predicted)));
    }
    sampleBucketPoseToRef(action, this.bucketProgress, a.bucketFull, firstPerson, a.speed > 0.2, this.walkPhase, this.bucketPose);
  }

  /**
   * World grip on the posed bucket: the right hand's fist around the bail handle, the left
   * palm against the rim's side (scoop) or cupped under the base (throw).
   */
  private bucketGripToRef(grip: "handle" | "rim" | "base", position: Vector3, rotation: Quaternion): void {
    const world = this.bucket.computeWorldMatrix(true);
    const at = grip === "handle" ? null : grip === "rim" ? BUCKET_RIM_GRIP : BUCKET_BASE_GRIP;
    Vector3.TransformCoordinatesFromFloatsToRef(at?.x ?? 0, at?.y ?? 0, at?.z ?? 0, world, position);
    // Bucket-local palm and finger directions.
    if (grip === "handle") { this.bucketPalm.set(0, 0, -1); this.bucketFingers.set(0, -1, 0); }
    else if (grip === "rim") { this.bucketPalm.set(1, 0, 0); this.bucketFingers.set(0, -1, 0); }
    else { this.bucketPalm.set(0, 1, 0); this.bucketFingers.set(0, 0, 1); }
    Vector3.TransformNormalToRef(this.bucketPalm, world, this.bucketPalm);
    Vector3.TransformNormalToRef(this.bucketFingers, world, this.bucketFingers);
    this.handFacingToRef(this.bucketPalm, this.bucketFingers, rotation);
  }

  /** Test diagnostics: eased body lean from upright (radians) and the submerged/stroking blends. */
  swimState(): { lean: number; submerged: number; stroking: number } {
    return { lean: this.torso.rotation.x, submerged: this.swimUnder, stroking: this.swimMove };
  }

  /**
   * Swim blends from the root's own rendered motion, so every client derives the
   * same pose: travel drives the stroke cycle, depth below the surface (or sinking)
   * selects the dive, and the travel direction pitches the submerged body.
   */
  private updateSwim(y: number, travel: number, dt: number, reset: boolean): void {
    if (reset || Number.isNaN(this.swimPrevY)) { this.swimPrevY = y; this.swimRate = this.swimHorizontal = 0; }
    const dy = y - this.swimPrevY;
    this.swimPrevY = y;
    if (dt <= 0) return;
    const k = 1 - Math.exp(-dt / 0.18);
    this.swimRate += (Math.max(-4, Math.min(4, dy / dt)) - this.swimRate) * k;
    this.swimHorizontal += (Math.min(6, travel / dt) - this.swimHorizontal) * k;
    // Swimmers live in the world frame, where the surface sits at the shared surface feet height.
    const depth = this.root.parent === null ? PLAYER.surfaceFeetY - y : 0;
    const under = Math.max(smooth((depth - 0.1) / 0.5), smooth(-this.swimRate / 1.2));
    this.swimUnder += (under - this.swimUnder) * k;
    this.swimMove += (smooth(Math.hypot(this.swimHorizontal, this.swimRate) / 1.2) - this.swimMove) * k;
    this.swimPitch = Math.max(-SWIM_MAX_TRAVEL_PITCH, Math.min(SWIM_MAX_TRAVEL_PITCH, Math.atan2(-this.swimRate, this.swimHorizontal)));
    // One stroke per audible stroke distance, so splashes and pulls share a cadence.
    this.swimPhase = (this.swimPhase + Math.hypot(travel, dy) / STROKE) % 1;
  }

  /** Treading upright, heads-up prone stroke, or head-first along the underwater travel direction. */
  private swimLean(): number {
    const surface = SWIM_TREAD_LEAN + (SWIM_SURFACE_LEAN - SWIM_TREAD_LEAN) * this.swimMove;
    const under = SWIM_HOVER_LEAN + (Math.PI / 2 + this.swimPitch - SWIM_HOVER_LEAN) * this.swimMove;
    return surface + (under - surface) * this.swimUnder;
  }

  /** Torso-relative head pitch: the face looks ahead above water, and stays in line with the spine below. */
  private swimHeadPitch(lean: number, pitch: number): number {
    const look = Math.max(-0.5, Math.min(0.5, pitch));
    const surface = Math.max(SWIM_NECK_LIMIT, 0.1 + look * 0.6 - lean);
    const under = -0.3 + look * 0.4;
    return surface + (under - surface) * this.swimUnder;
  }

  /** Breaststroke (surface or underwater) or sculling hands, posed in the leaning torso's frame. */
  private swimArmToRef(arm: RemoteArm, side: number): HandPose {
    const torsoWorld = this.torso.computeWorldMatrix(true), under = this.swimUnder, move = this.swimMove;
    sampleStroke(SURFACE_STROKE, this.swimPhase, side, this.swimPosition, this.swimPalm, this.swimFingers);
    sampleStroke(UNDERWATER_STROKE, this.swimPhase, side, this.local, this.palm, this.fingers);
    Vector3.LerpToRef(this.swimPosition, this.local, under, this.local);
    Vector3.LerpToRef(this.swimPalm, this.palm, under, this.palm);
    Vector3.LerpToRef(this.swimFingers, this.fingers, under, this.fingers);
    // Treading: palms down, sculling out and back in front of the chest.
    const scull = this.time * 5, s = Math.sin(scull), c = Math.cos(scull);
    this.swimPosition.set(side * (0.40 + 0.12 * s), 1.08 + 0.02 * c, 0.30 + 0.04 * c);
    this.swimPalm.set(side * 0.35 * c, -1, 0); this.swimFingers.set(side * 0.35, 0, 1);
    Vector3.LerpToRef(this.swimPosition, this.local, move, this.local);
    Vector3.LerpToRef(this.swimPalm, this.palm, move, this.palm);
    Vector3.LerpToRef(this.swimFingers, this.fingers, move, this.fingers);
    Vector3.TransformCoordinatesToRef(this.local, torsoWorld, arm.target);
    // Elbows stay high and outboard through the pull; out to the sides while sculling.
    this.scratch.set(side * (0.75 - 0.03 * move), 1.15 + 0.30 * move, -0.10 - 0.15 * move);
    Vector3.TransformCoordinatesToRef(this.scratch, torsoWorld, arm.pole);
    this.handFacingToRef(this.palm, this.fingers, this.toolRotation);
    Quaternion.FromRotationMatrixToRef(torsoWorld, this.scratchRotation);
    this.scratchRotation.multiplyToRef(this.toolRotation, arm.targetRotation);
    return "swim";
  }

  /** Body-frame frog kick (or slow treading cycle) for one foot, with its knee pole and pointed or flexed foot. */
  private swimFootToRef(i: number, side: number, hipY: number, lean: number): void {
    const kick = sampleLoop(FROG_KICK, this.swimPhase);
    const cos = Math.cos(lean), sin = Math.sin(lean), along = kick[2]!, dorsal = kick[3]!, flex = kick[4]!;
    // Legs trail along -spine (0, -cos, -sin); heels lift dorsally (0, sin, -cos); knees drop ventrally.
    this.local.set(side * kick[1]!, hipY - along * cos + dorsal * sin, -along * sin - dorsal * cos);
    this.swimKnee.set(side * 0.35, hipY - 0.45 * cos - 0.55 * sin, -0.45 * sin + 0.55 * cos);
    // Treading: each foot circles slowly beneath the hips, half a cycle apart.
    const tread = this.time * 3 + i * Math.PI, move = this.swimMove;
    this.swimTread.set(side * 0.13, hipY - 0.62 + 0.07 * Math.sin(tread), 0.08 + 0.12 * Math.cos(tread));
    this.swimTreadKnee.set(side * 0.2, hipY - 0.3, 0.6);
    Vector3.LerpToRef(this.swimTread, this.local, move, this.local);
    Vector3.LerpToRef(this.swimTreadKnee, this.swimKnee, move, this.swimKnee);
    // Pointed toes trail the leg; the kick flexes them to push water back.
    const footPitch = 0.55 + (lean + Math.PI / 2 * (1 - flex) - 0.55) * move;
    Quaternion.RotationYawPitchRollToRef(0, footPitch, 0, this.scratchRotation);
    this.rotation.multiplyToRef(this.scratchRotation, this.swimFootRotation);
  }

  /**
   * Action-frame hand rotation from where the palm faces and the fingers point.
   * Hanging palms face local -z with the index along local -y; `fingers` is
   * made perpendicular to `palm`.
   */
  private handFacingToRef(palm: Vector3, fingers: Vector3, out: Quaternion): void {
    palm.normalizeToRef(this.handAxisZ).scaleInPlace(-1);
    const along = Vector3.Dot(fingers, this.handAxisZ);
    fingers.subtractToRef(this.handAxisZ.scaleToRef(along, this.handAxisY), this.handAxisY);
    this.handAxisY.normalize().scaleInPlace(-1);
    Vector3.CrossToRef(this.handAxisY, this.handAxisZ, this.handAxisX);
    Matrix.FromXYZAxesToRef(this.handAxisX, this.handAxisY, this.handAxisZ, this.handBasis);
    Quaternion.FromRotationMatrixToRef(this.handBasis, out);
  }

  private blendArm(arm: RemoteArm, changed: boolean, transition: number): void {
    if (!this.initialized) {
      arm.offset.setAll(0);
      arm.shoulderOffset.setAll(0);
      arm.rotationOffset.copyFrom(this.identityRotation);
    } else if (changed) {
      arm.targetRotation.conjugateToRef(this.scratchRotation);
      arm.contact.subtractToRef(arm.target, this.scratch);
      this.scratch.rotateByQuaternionToRef(this.scratchRotation, arm.offset);
      arm.shoulder.subtractToRef(arm.targetShoulder, this.scratch);
      this.scratch.rotateByQuaternionToRef(this.scratchRotation, arm.shoulderOffset);
      this.scratchRotation.multiplyToRef(arm.rotation, arm.rotationOffset);
    }
    const t = smooth(transition);
    arm.offset.rotateByQuaternionToRef(arm.targetRotation, this.scratch);
    this.scratch.scaleInPlace(1 - t);
    arm.target.addToRef(this.scratch, arm.contact);
    arm.shoulderOffset.rotateByQuaternionToRef(arm.targetRotation, this.scratch);
    this.scratch.scaleInPlace(1 - t);
    arm.targetShoulder.addToRef(this.scratch, arm.shoulder);
    Quaternion.SlerpToRef(arm.rotationOffset, this.identityRotation, t, this.scratchRotation);
    arm.targetRotation.multiplyToRef(this.scratchRotation, arm.rotation);
  }

  /**
   * Feeds the first-person hand lag the camera's motion relative to the feet since the last
   * frame, in camera axes: head bob, sway, landing dip and crouch move it; walking, turning and
   * a moving deck do not. `reset` re-seats the hands on the camera.
   */
  private updateViewmodelLag(dt: number, reset: boolean): void {
    const camera = this.view!.computeWorldMatrix();
    camera.getTranslationToRef(this.scratch).subtractInPlace(this.root.computeWorldMatrix(true).getTranslationToRef(this.local));
    if (reset || !this.lagSeeded) this.viewLag.reset();
    else {
      this.scratch.subtractToRef(this.lagCamera, this.local);
      camera.invertToRef(this.lagInverse);
      Vector3.TransformNormalToRef(this.local, this.lagInverse, this.local);
      this.viewLag.update(this.local.x, this.local.y, this.local.z, dt);
    }
    this.lagCamera.copyFrom(this.scratch);
    this.lagSeeded = true;
  }

  pistolMuzzleToRef(out: Vector3): void {
    this.pistolMechanism.muzzleToRef(out);
  }

  /**
   * First person: the eye and action frames ride the camera, so the body's own
   * arms and tools are posed in view space; the head is hidden but still casts
   * its shadow. Null returns to the third-person head-driven frames.
   */
  setFirstPerson(camera: Camera | null): void {
    if (camera === this.view) return;
    this.view = camera;
    this.lagSeeded = false;
    if (camera) {
      for (const node of [this.eye, this.actionFrame, this.spyglass]) { node.parent = camera; node.position.setAll(0); }
      this.actionFrame.position.set(FIRST_PERSON_ACTION_FRAME.x, FIRST_PERSON_ACTION_FRAME.y, FIRST_PERSON_ACTION_FRAME.z);
      this.eye.rotation.setAll(0); this.actionFrame.rotation.setAll(0);
      this.spyglass.rotationQuaternion!.copyFrom(this.identityRotation);
      for (const mesh of this.headMeshes) this.hide(mesh);
    } else {
      this.eye.parent = this.torso; this.eye.position.set(0, 1.70, 0);
      this.actionFrame.parent = this.torso;
      this.actionFrame.position.set(THIRD_PERSON_ACTION_FRAME.x, THIRD_PERSON_ACTION_FRAME.y, THIRD_PERSON_ACTION_FRAME.z);
      this.spyglass.parent = this.torso;
      for (const mesh of this.headMeshes) this.show(mesh);
      for (const mesh of this.overlayMeshes) this.show(mesh);
      this.overlayHidden = false;
      this.setOverlayGroup(0);
    }
  }

  private hide(mesh: Mesh): void {
    if (!this.hiddenMasks.has(mesh)) this.hiddenMasks.set(mesh, mesh.layerMask);
    mesh.layerMask = FIRST_PERSON_HIDDEN_LAYER;
  }

  private show(mesh: Mesh): void {
    const mask = this.hiddenMasks.get(mesh);
    if (mask === undefined) return;
    mesh.layerMask = mask;
    this.hiddenMasks.delete(mesh);
  }

  /** First-person camera height above the feet, in the root's parent frame. */
  private cameraHeight(): number {
    this.scratch.copyFrom(this.view!.computeWorldMatrix().getTranslation());
    const parent = this.root.parent as TransformNode | null;
    if (parent) {
      parent.computeWorldMatrix(true).invertToRef(this.footFrameInverse);
      Vector3.TransformCoordinatesToRef(this.scratch, this.footFrameInverse, this.scratch);
    }
    return this.scratch.y - this.root.position.y;
  }

  private setOverlayGroup(group: number): void {
    if (group === this.overlayGroup) return;
    this.overlayGroup = group;
    for (const mesh of this.overlayMeshes) mesh.renderingGroupId = group;
  }

  setVisible(v: boolean): void {
    this.root.setEnabled(v);
    // A first-person spyglass and repair plank ride the camera, outside the root.
    if (!v) { this.spyglass.setEnabled(false); this.plank.setEnabled(false); this.initialized = false; this.footCycles[0] = this.footCycles[1] = -1; }
  }
  setShadow(on: boolean): void {
    if (on === this.shadowOn) return;
    this.shadowOn = on;
    for (const mesh of this.meshes) { if (on) this.env.addCaster(mesh); else this.env.removeCaster(mesh); }
  }
  dispose(): void { this.setFirstPerson(null); for (const mesh of this.meshes) this.env.removeCaster(mesh); this.root.dispose(false, false); }
}

/** A treasure chest (tools/models/chest.py) with softly glowing gold fittings. */
export class ChestModel {
  readonly root: TransformNode;
  private readonly meshes: Mesh[];
  private readonly env: Environment;
  private t = Math.random() * 10;

  constructor(scene: Scene, env: Environment, tier: number) {
    this.env = env;
    const fitting = tier === 0 ? env.mats.get("iron") : env.mats.get("gold", { emissive: tier === 1 ? 0.25 : 0.8 });
    const model = spawnModel(scene, env.mats, "chest", { materials: { gold: fitting } });
    this.root = model.root;
    this.meshes = model.meshes;
    for (const m of this.meshes) env.addCaster(m);
  }

  /** Bobbing when floating. */
  float(dt: number, reducedMotion: boolean): number {
    this.t += dt;
    return reducedMotion ? -0.2 : -0.25 + Math.sin(this.t * 1.7) * 0.08;
  }

  dispose(): void {
    for (const m of this.meshes) this.env.removeCaster(m);
    this.root.dispose(false, false);
  }
}

/** Battle royale floating loot: a supply barrel bobbing on the swell (balls or planks alike). */
export class LootModel {
  readonly root: TransformNode;
  private readonly meshes: Mesh[];
  private readonly env: Environment;
  private t = Math.random() * 10;

  constructor(scene: Scene, env: Environment) {
    this.env = env;
    const model = spawnScenery(scene, env.mats, "supplyBarrel", "tropical");
    this.root = model.root;
    this.meshes = model.meshes;
    for (const m of this.meshes) env.addCaster(m);
  }

  /** Bobbing on the swell. */
  float(dt: number, reducedMotion: boolean): number {
    this.t += dt;
    return reducedMotion ? -0.2 : -0.25 + Math.sin(this.t * 1.7) * 0.08;
  }

  dispose(): void {
    for (const m of this.meshes) this.env.removeCaster(m);
    this.root.dispose(false, false);
  }
}

/** A small dirt mound left by digging. */
export function digMound(scene: Scene, env: Environment): Mesh {
  const m = CreateSphere("mound", { diameter: 0.9, segments: 4 }, scene);
  m.scaling.y = 0.3;
  m.material = env.mats.get("dirt");
  sphereProjectUVs(m, SURFACE_PROFILES.ground.metres);
  m.isPickable = false;
  return m;
}
